import { withListOverflow } from "../components/ListOverflow.jsx";
export const WeeklyReviewMeetings = withListOverflow(WeeklyMeetingsListOwner);
import { useCallback, useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import {
  getReviewMeetings, markReviewMeetingHeld, editReviewMeetingLog, deleteReviewMeetingLog,
} from "../../data/reviewsClient.js";
import { emitCommandIndexStale } from "../../services/cacheBus.js";

const statusNames = {
  on_cadence: "On cadence", due_soon: "Due soon", overdue: "Overdue", never_held: "Never held",
};
const statusColors = {
  on_cadence: ["var(--v2-green)", T.ragGreenBg],
  due_soon: ["var(--v2-amber)", T.ragAmberBg],
  overdue: ["var(--v2-red)", T.ragRedBg],
  never_held: ["var(--v2-red)", T.ragRedBg],
};
const fmt = (date) => date
  ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${date}T12:00:00Z`))
  : "—";
const button = {
  border: "none", background: "none", color: "var(--v2-accent)", cursor: "pointer",
  fontSize: T.fsSm5, fontWeight: T.fwSemibold, padding: "4px 2px",
};

function WeeklyMeetingsListOwner({ userRole, pillar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [heldAt, setHeldAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const result = await getReviewMeetings();
    setData(result);
    setError("");
  }, []);
  useEffect(() => {
    let alive = true;
    getReviewMeetings().then((result) => { if (alive) setData(result); })
      .catch((err) => { if (alive) setError(err.message || "Could not load weekly meetings."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const open = (meeting, log = null) => {
    setEditing({ meeting, log });
    setHeldAt(log?.heldAt || new Date().toISOString().slice(0, 10));
    setNote(log?.note || "");
    setError("");
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const body = { held_at: heldAt, note };
      if (editing.log) await editReviewMeetingLog(editing.meeting.id, editing.log.id, body);
      else await markReviewMeetingHeld(editing.meeting.id, body);
      emitCommandIndexStale();
      await refresh();
      setEditing(null);
    } catch (err) {
      setError(err.message || "Could not save meeting.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this meeting log entry?")) return;
    setSaving(true);
    try {
      await deleteReviewMeetingLog(editing.meeting.id, editing.log.id);
      emitCommandIndexStale();
      await refresh();
      setEditing(null);
    } catch (err) {
      setError(err.message || "Could not delete meeting log.");
    } finally {
      setSaving(false);
    }
  };

  const bandColor = data?.band === "Strong" ? "var(--v2-green)"
    : data?.band === "Slipping" ? "var(--v2-amber)" : "var(--v2-red)";
  const weight = pillar?.status === "live" && Number.isFinite(pillar?.score)
    && Number.isFinite(pillar?.weightUsed) && pillar.weightUsed > 0
    ? `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 })
      .format(pillar.weightUsed <= 1 ? pillar.weightUsed * 100 : pillar.weightUsed)}%`
    : null;
  return (
    <section data-testid="weekly-review-meetings" aria-label="Weekly review meetings" style={{
      background: "var(--v2-card)", border: "1px solid var(--v2-line)", borderRadius: 16,
      boxShadow: "var(--v2-shadow-card)", padding: "24px 28px", marginTop: 18,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: T.fsMd, color: "var(--v2-dark)", margin: 0 }}>Weekly review meetings</h2>
        {data && <strong data-testid="weekly-meeting-score" style={{ color: bandColor }}>
          {data.score === null ? "No score" : `${data.score} / 100`} · {data.band || "Not scored"}
        </strong>}
      </div>
      <p style={{ fontSize: T.fsSm, color: "var(--v2-mid)", lineHeight: 1.5 }}>
        {weight
          ? `Weekly meeting cadence contributes to the Portfolio Command composite at its configured ${weight} weight.`
          : "Weekly meeting cadence is evaluated from the configured review meetings."}
        {" "}Each meeting contributes equally; overdue and never-held meetings put their covered areas at risk.
      </p>
      {loading && <p role="status">Loading weekly meetings…</p>}
      {error && <p role="alert" style={{ color: "var(--v2-red)" }}>{error}</p>}
      {!loading && !data && <button type="button" onClick={() => refresh().catch((err) => setError(err.message))}>Retry</button>}
      {data && <>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5 }}>
            <thead><tr>{["Meeting", "Cadence", "Covers", "Last held", "Next due", "Status", ...(userRole === "admin" ? ["Action"] : [])]
              .map((label) => <th key={label} scope="col" style={{
                textAlign: "left", padding: "10px 8px", color: "var(--v2-mid)",
                borderBottom: "1px solid var(--v2-line)", whiteSpace: "nowrap",
              }}>{label}</th>)}</tr></thead>
            <tbody>{data.meetings.map((meeting) => {
              const [fg, bg] = statusColors[meeting.status];
              return <tr key={meeting.id}>
                <td style={cell}><strong>{meeting.name}</strong></td>
                <td style={cell}>Weekly</td>
                <td style={cell}><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {meeting.covers.map((area) => <span key={area} style={{
                    padding: "3px 7px", borderRadius: 5, background: "var(--v2-tint)", color: "var(--v2-mid)",
                  }}>{area}</span>)}</div></td>
                <td style={cell}>{meeting.lastHeld ? fmt(meeting.lastHeld) : "Never held"}
                  {meeting.ageDays !== null && <small style={{ color: "var(--v2-mid)", display: "block" }}>
                    {meeting.ageDays} days ago</small>}</td>
                <td style={cell}>{fmt(meeting.nextDue)}</td>
                <td style={cell}><span style={{ color: fg, background: bg, padding: "4px 8px",
                  borderRadius: 6, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>
                  {statusNames[meeting.status]}</span></td>
                {userRole === "admin" && <td style={cell}>
                  <button style={button} type="button" onClick={() => open(meeting)}>Mark held</button>
                  {meeting.logs.length > 0 && <button style={button} type="button"
                    onClick={() => open(meeting, meeting.logs[0])}>Edit</button>}
                </td>}
              </tr>;
            })}</tbody>
          </table>
        </div>
        {data.meetings.length === 0 && <p>No active weekly meetings.</p>}
        <div style={{ marginTop: 16, padding: 14, border: "1px solid var(--v2-line)",
          borderRadius: 10, background: "var(--v2-tint)" }}>
          <strong style={{ color: "var(--v2-mid)", fontSize: T.fsSm }}>Coverage — are the key areas being reviewed?</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {data.coverage.map(({ area, atRisk }) => <span key={area} style={{
              color: atRisk ? "var(--v2-red)" : "var(--v2-green)",
              background: atRisk ? statusColors.overdue[1] : statusColors.on_cadence[1],
              borderRadius: 6, padding: "5px 10px", fontWeight: T.fwSemibold,
            }}>{atRisk ? "!" : "✓"} {area}{atRisk ? " — at risk" : ""}</span>)}
          </div>
        </div>
      </>}
      {userRole === "admin" && editing && <form onSubmit={save} aria-label={`Log ${editing.meeting.name}`}
        style={{ marginTop: 18, borderTop: "1px solid var(--v2-line)", paddingTop: 16 }}>
        <strong>{editing.log ? "Correct log entry" : "Mark held"} · {editing.meeting.name}</strong>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "end", gap: 12, marginTop: 10 }}>
          <label>Held on <input aria-label="Held on" type="date" required max={new Date().toISOString().slice(0, 10)}
            value={heldAt} onChange={(event) => setHeldAt(event.target.value)} /></label>
          <label>Note (optional) <input aria-label="Note (optional)" maxLength={2000}
            value={note} onChange={(event) => setNote(event.target.value)} /></label>
          <button type="submit" disabled={saving}>Save</button>
          <button type="button" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
          {editing.log && <button type="button" onClick={remove} disabled={saving}>Delete entry</button>}
        </div>
        {editing.log && <div style={{ marginTop: 12, color: "var(--v2-mid)" }}>
          Need to correct an older entry? {editing.meeting.logs.map((entry) =>
            <button key={entry.id} type="button" style={button} onClick={() => open(editing.meeting, entry)}>
              {fmt(entry.heldAt)}
            </button>)}
        </div>}
      </form>}
    </section>
  );
}

const cell = { padding: "12px 8px", borderBottom: "1px solid var(--v2-line)",
  color: "var(--v2-dark)", verticalAlign: "middle" };